import { useEffect, useState } from "react";
import { fetchWithAuth } from "../../../api-client/api_client";
import { showErrorToast } from "../../shared/Toasts";

interface ConductorResource<T> {
  data: T | null;
  isLoading: boolean;
}

// Shared GET-and-load helper for the conductor views: handles the loading flag,
// unmount/param-change guarding, and the error toast in one place so each
// component doesn't re-implement the same effect. Pass a null path to defer the
// fetch (e.g. until a dependency is known); omit errorMessage to fail silently.
// Bump `reloadToken` to force a refetch (e.g. after a write).
export function useConductorResource<T>(
  path: string | null,
  errorMessage?: string,
  reloadToken = 0
): ConductorResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!path) {
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    fetchWithAuth({
      path,
      method: "GET",
      onResponse: (response: T) => {
        if (!active) return;
        setData(response);
        setIsLoading(false);
      },
      onError: () => {
        if (!active) return;
        // Drop whatever was loaded before. A refetch that 404s means the
        // resource is gone (e.g. an override was just cleared), so keeping the
        // previous value would leave the UI asserting something untrue.
        setData(null);
        if (errorMessage) showErrorToast(errorMessage);
        setIsLoading(false);
      },
    });
    return () => {
      active = false;
    };
  }, [path, errorMessage, reloadToken]);

  return { data, isLoading };
}
